import React, { useMemo, useState } from 'react';

import { BuilderComponent } from '@builder.io/react';
import * as Sentry from '@sentry/browser';
import config from 'config';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';

import DmsTemplate from '~/acquisition/components/DmsTemplate';
import FtvModal from '~/acquisition/components/FtvModal';
import HomepageViewedEvent from '~/acquisition/components/HomepageViewedEvent';
import { PromoPickerModal } from '~/acquisition/components/PromoPicker';
import { getHomePageContent } from '~/acquisition/utils/getHomePageContent';
import { useHomePageContent } from '~/acquisition/utils/useHomePageContent';
import usePromoPicker, {
  PROMO_PICKER_MODAL_PARAMETER,
} from '~/acquisition/utils/usePromoPicker';
import PromoTrackingWrapper from '~/builder/components/PromoTrackingWrapper/PromoTrackingWrapper';
import { getDeprecatedFieldClasses } from '~/builder/utils/getDeprecatedFieldClasses';
import { isBuilderContentReady } from '~/builder/utils/isBuilderContentReady';
import {
  registerBuilderComponents,
  registerBuilderMenu,
  allComponents,
} from '~/builder/utils/registerBuilderComponents';
import { useBuilderContent } from '~/builder/utils/useBuilderContent';
import { useTrackBuilderTestOnContentLoaded } from '~/builder/utils/useTrackBuilderTestOnContentLoaded';
import DefaultLayout from '~/layout/components/DefaultLayout';
import { AssortmentTestProvider } from '~/pdp/context/AssortmentTestContext';
import featureNames from '~/shared/constants/featureFlags';
import { getConfigValueSSR } from '~/shared/utils/config';
import { acceptRAFInvite } from '~/techstyle-shared/react-marketing';
import { getSession } from '~/techstyle-shared/redux-core';

import createPage from '../registry';

const dynamicDefineMessages = defineMessages;
const seo = dynamicDefineMessages({
  title: {
    id: 'site_seo_title.homepage_title',
    defaultMessage: config.get('public.brand.label'),
  },
  description: {
    id: 'site_seo_description.homepage_description',
    defaultMessage: `Welcome to ${config.get('public.brand.label')}!`,
  },
});

function HomePage({
  dmg,
  dmInfo,
  displayPage,
  builderJson,
  builderStateData = {},
}) {
  const isPostReg = displayPage === 'postreg';

  // adjust getHomePageContent content to be in a hook
  const {
    builderJson: clientBuilderJson,
    defaultBuilderJson,
    isLoading,
  } = useHomePageContent({ dmg, dmInfo });

  const trackBuilderTestOnContentLoaded = useTrackBuilderTestOnContentLoaded();

  const {
    isEnabled: isPromoPickerModalEnabled,
    isFirstPageView,
    showModal: showPromoPickerModal,
    wasClosed: wasPromoPickerClosed,
  } = usePromoPicker();

  /**
   * @var queryParams
   * @description apply `PROMO_PICKER_MODAL_PARAMETER` to the URL if the user clicks on
   * an FTV modal option and it is the users first page view.
   */
  const queryParams =
    isPromoPickerModalEnabled && isFirstPageView
      ? {
          [PROMO_PICKER_MODAL_PARAMETER]: 1,
        }
      : null;

  const [ftvModalOutsideClicked, setFtvModalOutsideClicked] = useState(false);
  const onFtvModalClosed = () => {
    setFtvModalOutsideClicked(true);
  };

  // determine which builder json to use
  const builderJsonToShow = useMemo(() => {
    if (clientBuilderJson) {
      return clientBuilderJson;
    } else if (defaultBuilderJson) {
      return defaultBuilderJson;
    } else {
      return builderJson;
    }
  }, [builderJson, clientBuilderJson, defaultBuilderJson]);

  const { content, locale } = useBuilderContent(builderJsonToShow);
  const showBuilder = isBuilderContentReady({ isLoading, content });
  const showDm = !showBuilder && displayPage === 'dm' && !isLoading;

  const deprecatedBuilderFields = useMemo(() => {
    return getDeprecatedFieldClasses(builderJsonToShow);
  }, [builderJsonToShow]);

  let noHeader = false;
  let noFooter = false;
  let showFtvModal = false;

  // builder rendering logic
  if (showBuilder) {
    const {
      title = config.get('public.brand.label'),
      description = title,
      showFtvModal: inputShowFtvModal,
      hideHeader,
      hideFooter,
    } = content?.data ?? {};
    showFtvModal = inputShowFtvModal;
    seo.title = title;
    seo.description = description;
    noHeader = hideHeader;
    noFooter = hideFooter;
  }

  /**
   * @var showPPP
   * @description renders PPP if the following business logic conditions are satisfied:
   * - The first condition takes business logic as defined in `usePromoPicker`.
   * - The second condition supports if a user clicks outside of the FTV modal which does not
   * apply a URL parameter for the display of PPP but business requirements call for the display
   * of PPP regardless.
   * - The third condition supports FTV pages that do not include an FTV modal to interract
   * with beforehand.
   */
  const showPPP =
    showPromoPickerModal ||
    (isPromoPickerModalEnabled && ftvModalOutsideClicked) ||
    (isPromoPickerModalEnabled && !wasPromoPickerClosed && !showFtvModal);

  // right now, defaultDMG should be the only thing for the homepage
  return (
    <DefaultLayout
      title={seo.title}
      description={seo.description}
      showPostRegTimer={!isPostReg}
      showMobilePostRegTimer={false}
      data-builder-deprecated-field={deprecatedBuilderFields.join()}
      noHeader={noHeader}
      noFooter={noFooter}
    >
      <HomepageViewedEvent displayPage={displayPage} />
      <PromoTrackingWrapper>
        {showBuilder && (
          <AssortmentTestProvider
            activeAssortmentTests={
              builderStateData?.activeAssortmentTests || []
            }
          >
            <BuilderComponent
              model="page"
              options={{
                enrich: true,
              }}
              content={content}
              locale={locale}
              data={builderStateData}
              contentLoaded={trackBuilderTestOnContentLoaded}
            />
          </AssortmentTestProvider>
        )}
        {showDm && <DmsTemplate dmgCode={dmg} dmInfo={dmInfo} />}
      </PromoTrackingWrapper>
      {showPPP ? (
        <PromoPickerModal />
      ) : (
        <FtvModal
          onModalClosed={onFtvModalClosed}
          showFtvModal={showFtvModal}
          additionalQueryParams={queryParams}
        />
      )}
    </DefaultLayout>
  );
}

HomePage.propTypes = {
  builderJson: PropTypes.object,
  builderStateData: PropTypes.object,
  displayPage: PropTypes.string,
  dmInfo: PropTypes.object,
  dmg: PropTypes.string,
};

HomePage.getInitialProps = async (ctx) => {
  const { query } = ctx;
  const session = getSession(ctx.store.getState());
  const { isLoggedIn } = session;

  // Disposition and loyalty are prefetched by fableticsPageExtension during
  // Phase 2. getBuilderJsonSSR (via getHomePageContent) and getExperienceId
  // dedup via networkStatus if already loaded.

  // handle refer-a-friend invite code acceptance for logged out users
  if (!isLoggedIn && query?.raf_code) {
    try {
      await ctx.store.dispatch(acceptRAFInvite({ code: query.raf_code }));
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  const [homepageContent, activeAssortmentTests] = await Promise.all([
    getHomePageContent(ctx, isLoggedIn),
    getConfigValueSSR(ctx, 'public.brand.assortmentTests'),
  ]);

  const { builderJson, dmg, dmInfo, displayPage, builderStateData } =
    homepageContent;

  return {
    dmg,
    dmInfo,
    displayPage,
    isLoggedIn,
    builderJson,
    builderStateData: {
      ...builderStateData,
      activeAssortmentTests: activeAssortmentTests || [],
    },
  };
};

registerBuilderComponents(allComponents);
registerBuilderMenu();

export default createPage(HomePage, {
  prefetchAssets: [
    'Logged_Out_Homepage_Pop_Up__Womens__mobile_',
    'Logged_Out_Homepage_Pop_Up__Womens__desktop_',
    'Logged_Out_Homepage_Pop_Up__Mens__mobile_',
    'Logged_Out_Homepage_Pop_Up__Mens__desktop_',
    'Logged_Out_Homepage_Pop_Up__Scrubs__mobile_',
    'Logged_Out_Homepage_Pop_Up__Scrubs__desktop_',
  ],
  prefetchFeatures: [featureNames.DENIM_INTEREST],
  resourceBundles: [
    'grid',
    'grid_static',
    'grid_dynamic',
    'member_box',
    'site_product_filter',
    'site_pdp_callout',
    'collection_page',
    'BMIG_Vip_Homepage_Womens',
    'BMIG_Vip_Homepage_Mens',
    'Marketing_card',
    'bill_me_now',
    'men_women_modal',
    'post_reg',
    'size_guides',
    'ppp',
    'signup',
    'site_product_grid',
  ],
});
